import React from 'react';

interface Position {
  id: string;
  name: string;
  fix: number;
  sti: number;
  ltip: string;
  reliability_score: number;
}

interface Company {
  id: string;
  name: string;
  sector: string;
  positions: Position[];
}

interface CompaniesTableProps {
  companies: Company[];
}

const CompaniesTable: React.FC<CompaniesTableProps> = ({ companies }) => {
  // Функция для получения уровня надежности по score
  const getReliabilityLevel = (score: number) => {
    if (score >= 0.8) return { level: 'Excellent', text: 'Очень высокая достоверность (Excellent)', color: '#073763' };
    if (score >= 0.5) return { level: 'High', text: 'Высокая достоверность (High)', color: '#1155cc' };
    if (score >= 0.3) return { level: 'Moderate', text: 'Умеренная достоверность (Moderate)', color: '#6d9eeb' };
    return { level: 'Low', text: 'Информация требует проверки (Low)', color: '#999999' };
  };

  // Группируем компании по секторам
  const sectorStats = companies.reduce((acc, company) => {
    if (!acc[company.sector]) {
      acc[company.sector] = {
        companies: [],
        count: 0,
        reliabilityScores: []
      };
    }
    acc[company.sector].companies.push(company.name);
    acc[company.sector].count++;
    
    // Вычисляем средний reliability_score по всем позициям компании
    if (company.positions && company.positions.length > 0) {
      const avgCompanyReliability = company.positions.reduce((sum, pos) => sum + pos.reliability_score, 0) / company.positions.length;
      acc[company.sector].reliabilityScores.push(avgCompanyReliability);
    }
    
    return acc;
  }, {} as Record<string, { companies: string[]; count: number; reliabilityScores: number[] }>);

  // Сортируем секторы по количеству компаний (убывание)
  const sectorEntries = Object.entries(sectorStats).sort((a, b) => b[1].count - a[1].count);
  const totalCompanies = companies.length;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      border: '1px solid #e0e0e0'
    }}>
      <h3 style={{
        fontSize: '20px',
        fontWeight: '600',
        color: '#1a237e',
        margin: '0 0 20px 0',
        textAlign: 'left',
        fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <i className="fas fa-building" style={{ fontSize: '18px' }}></i>
        КОМПАНИИ
      </h3>
      
      <div style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px'
        }}>
          <thead>
            <tr style={{
              borderBottom: '2px solid #e0e0e0'
            }}>
            <th style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#1a237e',
              width: '150px'
            }}>
              Сектор
            </th>
            <th style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#1a237e',
              width: '100px'
            }}>
              Количество
            </th>
            <th style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#1a237e'
            }}>
              Описание
            </th>
            <th style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#1a237e',
              width: '150px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <span>Надежность</span>
                <button
                  style={{
                    background: 'none',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#1a237e',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #1a237e',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1a237e';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.color = '#1a237e';
                  }}
                  title="Информация о надежности данных"
                >
                  ?
                </button>
              </div>
            </th>
            <th style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontWeight: '600',
              color: '#1a237e',
              width: '80px'
            }}>
              %
            </th>
          </tr>
        </thead>
        <tbody>
          {sectorEntries.map(([sector, data], index) => {
            const percentage = ((data.count / totalCompanies) * 100).toFixed(1);
            
            return (
              <tr key={index} style={{
                borderBottom: '1px solid #f0f0f0'
              }}>
                <td style={{
                  padding: '12px 8px',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  {sector}
                </td>
                <td style={{
                  padding: '12px 8px',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  {data.count}
                </td>
                <td style={{
                  padding: '12px 8px',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '13px',
                  lineHeight: '1.4'
                }}>
                  {data.companies.join(', ')}
                </td>
                <td style={{
                  padding: '12px 8px',
                  verticalAlign: 'middle',
                  textAlign: 'left',
                  color: '#666'
                }}>
                  {(() => {
                    if (data.reliabilityScores.length === 0) return '-';
                    
                    // Вычисляем средний reliability_score по всем компаниям сектора
                    const avgSectorReliability = data.reliabilityScores.reduce((sum, score) => sum + score, 0) / data.reliabilityScores.length;
                    const reliabilityInfo = getReliabilityLevel(avgSectorReliability);
                    
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: reliabilityInfo.color,
                          borderRadius: '2px',
                          flexShrink: 0
                        }}></div>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: '#666'
                        }}>
                          {reliabilityInfo.text}
                        </span>
                      </div>
                    );
                  })()}
                </td>
                <td style={{
                  padding: '12px 8px',
                  verticalAlign: 'middle',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  {percentage}%
                </td>
              </tr>
            );
          })}
        </tbody>
              </table>
      </div>
      
    </div>
  );
};

export default CompaniesTable;
